import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Modal, Button, Form, Input, Dropdown, Checkbox } from "semantic-ui-react";

import { ConfigurableQuestionnaireKey, QuestionType, questionTypeOptions } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { toast } from "@teamprevent/common/toasts";

import { ApplicationState } from "../../applicationState";
import { AdditionalQuestionCreateModel } from "../model";
import { questionnaireConfigAddAdditionalQuestion } from "../actions";
import { basePathSelector } from "../selectors";

import { push } from "redux-little-router";

export const CreateAdditionalQuestionFormModal: React.FunctionComponent = () => {

    const customerId = useSelector((state: ApplicationState) => state.router.params?.customerId);
    const questionnaireType = useSelector((state: ApplicationState) => state.router.params?.questionnaireType as ConfigurableQuestionnaireKey);
    const open = useSelector((state: ApplicationState) => state.router.pathname.endsWith("/create"));
    const basePath = useSelector((state: ApplicationState) => basePathSelector(state));

    const [showErrors, setShowErrors] = React.useState<boolean>(false);
    const [questionText, setQuestionText] = React.useState<string>(null);
    const [questionType, setQuestionType] = React.useState<QuestionType>(null);
    const [dropdownOptionsCombined, setDropdownOptionsCombined] = React.useState<string>(null);
    const [dropdownOptions, setDropdownOptions] = React.useState<string[]>([]);
    const [isMandatory, setIsMandatory] = React.useState<boolean>(false);

    const dispatch = useDispatch();

    const resetState = () => {
        setShowErrors(false);
        setQuestionText(null);
        setQuestionType(null);
        setDropdownOptionsCombined(null);
        setDropdownOptions([]);
    };

    const close = () => {
        dispatch(push(basePath, {}));
        resetState();
    };

    const save = async () => {

        setShowErrors(true);

        if (!customerId || !questionnaireType || !questionText || !questionType) {
            toast.error("Some of question settings are invalid.");
            return;
        }

        const noDropdownOptions = !dropdownOptions || dropdownOptions.length === 0;
        if (questionType === QuestionType.DropdownQuestion && noDropdownOptions) {
            toast.error("You need to provide dropdown options (separated by ',')");
            return;
        }

        const questionModel: AdditionalQuestionCreateModel = {
            customerId,
            questionnaireType,
            questionText: questionText.trim(),
            questionType,
            options: dropdownOptions,
            isMandatory
        };
        await dispatch(questionnaireConfigAddAdditionalQuestion(questionModel));

        dispatch(push(basePath, {}));
        resetState();
    };

    const onDropdownOptionsChange = React.useCallback((event: React.SyntheticEvent<HTMLElement>, { value }: any) => {
        setDropdownOptionsCombined(value);

        const options = (value as string)?.split(",").map(o => o.trim());
        setDropdownOptions(options);
    }, []);

    return (
        <Modal size="small" open={open} onClose={close} closeOnDimmerClick={false}>
            <Modal.Header>Create additional question</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>Question text*</label>
                        <Input
                            value={questionText}
                            showErrors={showErrors}
                            required
                            onChange={(e, d) => setQuestionText(d.value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Question type*</label>
                        <Dropdown
                            value={questionType}
                            showErrors={showErrors}
                            required
                            onChange={(e, d) => setQuestionType(d.value as QuestionType)}
                            options={questionTypeOptions}
                            fluid
                            selection
                            labeled
                        />
                    </Form.Field>
                    {questionType === QuestionType.DropdownQuestion &&
                        <Form.Field>
                            <label>Answer*</label>
                            <Input
                                value={dropdownOptionsCombined}
                                showErrors={showErrors}
                                required
                                onChange={onDropdownOptionsChange}
                            />
                        </Form.Field>
                    }
                    <Form.Field>
                        <Checkbox
                            label="Mandatory*"
                            checked={isMandatory}
                            showErrors={showErrors}
                            required
                            onChange={(e, d) => setIsMandatory(d.checked)}
                            fluid
                            selection
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={close}>Cancel</Button>
                <Button onClick={save}>Save</Button>
            </Modal.Actions>
        </Modal>
    );
};
